import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import UITimePicker from '../ui-controls/time-field';
import moment from "moment";

// Picker values can be a moment, a Date, or a clock string. Rebuild with this
// moment so a second copy of moment does not format as "Invalid date".
const toLocalMoment = (raw) => {
  if (raw == null || raw === "") return null;

  const hourFn = raw.hours || raw.hour;
  if (typeof hourFn === "function") {
    // Read the clock in local mode, including PM. A UTC moment's .hours()
    // is the stored hour and would turn 5:00 PM into 05:00.
    const local = typeof raw.clone === "function" ? raw.clone().local() : raw;
    const minuteFn = local.minutes || local.minute;
    const localHourFn = local.hours || local.hour;
    const built = moment({
      year: typeof local.year === "function" ? local.year() : moment().year(),
      month:
        typeof local.month === "function" ? local.month() : moment().month(),
      date: typeof local.date === "function" ? local.date() : moment().date(),
      hour: localHourFn.call(local),
      minute: typeof minuteFn === "function" ? minuteFn.call(local) : 0,
      second: 0,
    });
    return built.isValid() ? built : null;
  }

  if (raw instanceof Date) {
    const built = moment(raw);
    return built.isValid() ? built : null;
  }

  const parsed = moment(
    raw,
    ["YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A", "HH:mm", "hh:mm A", "h:mm A"],
    true
  );
  return parsed.isValid() ? parsed : null;
};

const TimeFilter = ({ filterValue, filterDelay, onChange, render, readOnly, style }) => {
  const [value, setValue] = useState(filterValue ? filterValue.value : '');

  const debouncedOnValueChange = useCallback(
    debounce((value) => {
      onChange(Object.assign({}, filterValue, { value }));
    }, filterDelay >= 1 ? filterDelay : 0, {
      leading: false,
      trailing: true
    }),
    [filterDelay, onChange, filterValue]
  );

  useEffect(() => {
    return () => {
      debouncedOnValueChange.cancel();
    };
  }, [debouncedOnValueChange]);

  const handleChange = (time) => {
    const raw = time?.target?.value;
    const picked = toLocalMoment(raw);
    if (!picked) {
      debouncedOnValueChange.cancel();
      onChange(Object.assign({}, filterValue, { value: "" }));
      setValue(null);
      return;
    }

    // Local clock time. generateGqlParams applies the UTC offset.
    debouncedOnValueChange(picked.format("YYYY-MM-DD HH:mm"));
    setValue(picked);
  };

  const inputProps = {
    readOnly,
    style: Object.assign({}, { minWidth: 0 }, style),
    value: filterValue ? value : null,
    onChange: handleChange,
    size: "small",
    showClear: true,
    sx:{
        width: "100%",
        background: 'white', 
        borderColor: 'transparent',
        height: "32px",
        ".MuiInputBase-root": {
            height: "32px",
            fontSize: "14px"
        },
    },
    iconSx: {
        width: "18px",
        height: "18px",
        color: "var(--base-100)"
    },
    format:"hh:mm A",
    placeholder: "Select Time",
    className: "inovua-react-datagrid__column-header__filter inovua-react-datagrid__column-header__filter--string",
    type: "time"
  };

  return render(<UITimePicker {...inputProps} />);
};

export default TimeFilter;
